import config from "appConfig";

const theory = `<h3 class=sectionHead>Direct discrete convolution</h3>
<p>For interactive acoustic virtual reality simulations the convolution process needs to be performed in real time with low delays (which are also known as “latencies”). The convolution algorithm can be defined by a straightforward approach by computing the convolution integral. In its discrete form, it reads:
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>y</mi>
            <mo class=MathClass-open>(</mo>
            <mi>n</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>k</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>N</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </munderover>
            <msub>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mi>k</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>⋅</mo>
            <msub>
               <mrow>
                  <mi>x</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi>k</mi>
               </mrow>
            </msub>
            <mi>h</mi>
            <mo class=MathClass-open>(</mo>
            <mi>n</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-open>[</mo>
            <msub>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mi>N</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-close>]</mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   The output signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>y</mi>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is calculated from processing the input signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   with the FIR filter
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   , the length of which is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
   </math>
   . The typical block diagram of this kind of FIR filter is shown in <a href="#" data-img-src="${config.ASSETS_URL}/03/18/theory/figure-1.svg" data-img-alt="Figure 1. Block diagram of a FIR filter in direct form I (so-called first form) also known as a tapped-delay-line.">Figure 1</a>. This layout is called direct form I or tapped-delay-line (TDL). The input samples
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mo class=MathClass-bin>+</mo>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <mi>…</mi>
   </math>
   are stored in delays, represented by the units
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>z</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msup>
   </math>
   . The latest sample and all past samples are multiplied with the filter coefficients
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <mi>…</mi>
      <mo class=MathClass-punc>,</mo>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mi>N</mi>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   (which are called taps) and then added up (i.e. a simple summation) to form the output samples
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>y</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <msub>
         <mrow>
            <mi>y</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mo class=MathClass-bin>+</mo>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <mi>…</mi>
   </math>
   . The number of filter coefficients (taps) is denoted by N. The order of the filter is defined as the length of the filter minus one (e.g.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
   </math>
   =5 for a fourth order filter).
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/03/18/theory/figure-1.svg" alt="Figure 1. Block diagram of a FIR filter in direct form I (so-called first form) also known as a tapped-delay-line."><figcaption class=caption>Figure 1. Block diagram of a FIR filter in direct form I (so-called first form) also known as a tapped-delay-line.</figcaption></figure>
<p>
   Image a continuous input audio stream from the left. As soon as the first input sample,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open>(</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>
   arrives, the resulting output sample,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>y</mi>
      <mo class=MathClass-open>(</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>
   , is multiplied with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-open>(</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>
   and sent to the output for D/A conversion and audio reproduction. Except for the time required for multiplication and storage, there is no computational delay (latency). This is called “low-latency” processing. As long as the latency is far below the order of magnitude of 50 ms, it can be named “real-time convolution”.<br class=newline>
<p>
   For longer FIR filters, however, the computational load might be rather high. For each sample of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mi>N</mi>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
   </math>
   multiplications and additions are required. Depending on the CPU or signal processor speed, this may lead to a processing bottleneck.<br class=newline>
<h3 class=sectionHead>Fast convolution</h3>
<p>
   Usually, the audio processing on ordinary computer systems cannot be done in a sample-wise manner. Several stages of buffering exist between the CPU and the digital to analogue (DA) converters in the audio devices. This makes it necessary to process blocks of samples rather than individual samples. The data is exchanged with the audio hardware is performed using streams. An audio stream has a fixed sampling rate,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   , and consists of a continuous sequence of sample blocks, one sample block for each channel. Here the channel is representing the number of output streams and it depends on the reproduction system. For example, for headphone reproduction n is the number of channels, which is two (left channel and right channel). Each block contains the same number of samples which referred to the streaming block length or buffer size. Typical values for real-time applications lie down in the range of 64 to 512 samples. Given a sampling rate
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   and a block length
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>B</mi>
   </math>
   , the block duration is given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>B</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>B</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   . The audio device continuously demands the next output block in intervals of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>B</mi>
         </mrow>
      </msub>
   </math>
   seconds. Hence, all processing of the samples of length
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>B</mi>
   </math>
   must be finished within this amount of time, otherwise, dropouts occur in the audio output stream.<br class=newline>
<p>
   The concept of FFT-based fast convolution was proposed in 1965. <a href="#" data-img-src="${config.ASSETS_URL}/03/18/theory/figure-1.svg" data-img-alt="Figure 2. Block diagram of fast convolution using the Fast Fourier Transform (FFT).">Figure 2</a> shows the algorithm for computing the linear convolution of a length M signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and a length
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
   </math>
   filter impulse response
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   using FFT. A transform size
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>K</mi>
      <mo class=MathClass-rel>≥</mo>
      <mi>M</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>N</mi>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
   </math>
   is selected (usually the next larger power of two). Both operands
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are appended with zeros (padding) to the length
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>K</mi>
   </math>
   . Then these are transformed into the frequency domain using K-point FFT. The
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>K</mi>
   </math>
   DFT coefficients are multiplied pair by pair. The result is transformed back into the time domain using a K-point IFFT. From the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>K</mi>
   </math>
   values only the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>M</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>N</mi>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
   </math>
   left-most samples are extracted.<br class=newline>
<p>
   Unless the convolutions are very short (e.g. less than 32 samples), this algorithm is much faster than direct time-domain filtering. The FFT transform size is often chosen to be the next larger power of two. However, non-power-of-two sizes can be of an advantage if the transform size has a few simple prime factors in its prime factorization (e.g.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>320</mn>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mn>6</mn>
         </mrow>
      </msup>
      <mo class=MathClass-bin>⋅</mo>
      <msup>
         <mrow>
            <mn>5</mn>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msup>
   </math>
   ). The fastest size can only be determined by benchmarks on the target computer.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/03/18/theory/figure-1.svg" alt="Figure 2. Block diagram of fast convolution using the Fast Fourier Transform (FFT)."><figcaption class=caption>Figure 2. Block diagram of fast convolution using the Fast Fourier Transform (FFT).</figcaption></figure>`;

export default theory;
